import React, { useState, useRef } from 'react';
import { useAuth } from '../lib/AuthProvider';
import toast from 'react-hot-toast';
import { Bot, AlertTriangle } from 'lucide-react';
import { Header } from './Header';
import { StrategicPartners } from './StrategicPartners';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { supabase } from '../lib/supabase';

const HCAPTCHA_SITE_KEY = '183ece9a-758a-44be-9b72-b30c0f62d8be';
const REQUIRE_CAPTCHA = import.meta.env.VITE_REQUIRE_CAPTCHA !== 'false';

export function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<string | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const captchaRef = useRef<HCaptcha>(null);
  const { signIn } = useAuth();

  const checkIfBlocked = async () => {
    try {
      const { data, error } = await supabase.rpc('check_login_blocked', {
        p_email: email,
        p_ip_address: null
      });

      if (error) throw error;

      if (data?.blocked) {
        setIsBlocked(true);
        setBlockedUntil(data.blocked_until);
        const blockedTime = new Date(data.blocked_until);
        const minutes = Math.ceil((blockedTime.getTime() - Date.now()) / 60000);
        toast.error(`Account blocked for ${minutes} minutes due to too many failed attempts`);
        return true;
      }

      setFailedAttempts(data?.failed_attempts || 0);
      return false;
    } catch (error) {
      console.error('Error checking block status:', error);
      return false;
    }
  };

  const recordAttempt = async (success: boolean) => {
    try {
      const { data, error } = await supabase.rpc('record_login_attempt', {
        p_email: email,
        p_success: success,
        p_ip_address: null,
        p_user_agent: navigator.userAgent
      });

      if (error) throw error;

      if (data?.blocked) {
        setIsBlocked(true);
        setBlockedUntil(data.blocked_until);
      } else if (!success) {
        setFailedAttempts(data?.failed_attempts || 0);
      }

      return data;
    } catch (error) {
      console.error('Error recording attempt:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (REQUIRE_CAPTCHA && !captchaToken) {
      toast.error('Please complete the captcha verification');
      return;
    }

    const blocked = await checkIfBlocked();
    if (blocked) {
      return;
    }

    setIsLoading(true);

    try {
      await signIn(email, password);
      await recordAttempt(true);
      toast.success('Signed in successfully');
      setFailedAttempts(0);
      setIsBlocked(false);
    } catch (error) {
      console.error('Auth error:', error);

      const attemptData = await recordAttempt(false);

      if (attemptData?.blocked) {
        toast.error(attemptData.message);
      } else if (attemptData?.failed_attempts) {
        const remaining = 3 - attemptData.failed_attempts;
        toast.error(`Invalid credentials. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining before lockout.`);
      } else {
        toast.error(error instanceof Error ? error.message : 'Authentication failed');
      }

      setCaptchaToken(null);
      captchaRef.current?.resetCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex items-center space-x-3">
              <Bot className="w-10 h-10 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold">AI Agent HUB</h1>
                <p className="text-sm text-gray-500">Social Media Scheduler</p>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              Sign in to your account
            </h2>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {isBlocked && blockedUntil && (
              <div className="rounded-md bg-red-50 border border-red-200 p-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800">Account Temporarily Blocked</h3>
                    <p className="text-sm text-red-700 mt-1">
                      Too many failed login attempts. Please try again after{' '}
                      {new Date(blockedUntil).toLocaleTimeString()}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {failedAttempts > 0 && !isBlocked && (
              <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                <p className="text-sm text-yellow-800">
                  {failedAttempts} failed attempt{failedAttempts !== 1 ? 's' : ''}.
                  {' '}{3 - failedAttempts} remaining before 10-minute lockout.
                </p>
              </div>
            )}

            <div className="rounded-md shadow-sm space-y-4">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  disabled={isBlocked}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                  placeholder="Email address"
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  disabled={isBlocked}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                  placeholder="Password"
                />
              </div>
            </div>

            {REQUIRE_CAPTCHA && (
              <div className="flex justify-center">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITE_KEY}
                  onVerify={handleCaptchaVerify}
                  onExpire={handleCaptchaExpire}
                />
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading || isBlocked || (REQUIRE_CAPTCHA && !captchaToken)}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="text-center space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
                </div>
              </div>
              
              <div className="text-sm">
                <a
                  href="https://panel.aiagenthub.pl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500"
                >
                  Visit panel.aiagenthub.pl to join the platform
                </a>
              </div>
            </div>
          </form>
        </div>
      </div>

      <StrategicPartners />
    </div>
  );
}